// src/components/QueryParamForm.jsx
'use client';
import React from 'react';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import Link from "next/link";

/**
 * A generic component to manage form submission and update URL query parameters.
 * Form fields passed as children must have a 'name' attribute.
 */
export function QueryParamForm({
   formId = 'query-param-form-id',
   children
}) {
   const router = useRouter();
   const pathname = usePathname();
   // Get the current search parameters from the URL
   const searchParams = useSearchParams();
   
   const handleSubmit = (event) => {
      event.preventDefault();
      
      // 1. Get form data
      const formData = new FormData(event.currentTarget);
      
      // Create a new URLSearchParams object based on existing params
      const params = new URLSearchParams(searchParams.toString());
      
      // 2. Iterate through form data and update query params
      for (const [name, value] of formData.entries()) {
         const stringValue = String(value).trim();
         
         // If the value is empty, remove the parameter from the URL
         if (stringValue === '' || stringValue === 'all') {
            params.delete(name);
         } else {
            // Otherwise, set or update the parameter
            params.set(name, stringValue);
         }
      }
      
      // 3. Construct the new URL
      const queryString = params.toString();
      const newUrl = `${pathname}${queryString ? '?' + queryString : ''}`;
      
      // 4. Update the route using router.replace to avoid clogging browser history
      router.replace(newUrl);
   };
   
   /**
    * Function to map over children and inject the defaultValue prop
    * by reading the current URL's searchParams.
    */
   const childrenWithDefaults = React.Children?.map(children, child => {
      // Check if the child is a valid React element and has a 'name' prop
      if (React.isValidElement(child) && child.props.name) {
         const paramName = child.props.name;
         // Get the value from the URL or default to an empty string
         const initialValue = searchParams.get(paramName) || '';
         
         // Clone the element and inject the defaultValue
         return React.cloneElement(child, {
            ...child.props,
            // Use defaultValue to allow the input to be uncontrolled by React state
            // but initialized by the URL param
            defaultValue: initialValue,
            // Ensure form elements can be submitted
            form: formId, // Optional: link element to the form by ID
         });
      }
      return child;
   });
   
   return (
      <form onSubmit={handleSubmit} id={formId} className="d-flex flex-wrap justify-content-end gap-2">
         {childrenWithDefaults}
         <button type="submit" className="btn btn-success"><i className="fa fa-search me-2"/> Apply Filters</button>
         
         {searchParams.toString() && (
            <Link
               href={pathname}
               className="btn btn-danger"><i className="fa fa-times me-2"/> Reset Filters
            </Link>
         )}
         
      </form>
   );
}